import { Button, message, Pagination, PaginationProps, Select, Table, TableColumnsType, TableProps, Typography } from "antd";
import { useEffect, useState } from "react";
import { canEditTable, canViewTable } from "../../PermissionCheck/PermissionCheck";
import { BsPencilSquare } from "react-icons/bs";
import { componentPalette } from "src/theme/component-palette";
import useNavigationMenu from "src/hook/useNavigationMenu";
import {
  DataTable,
  EmissionControlDetail,
  Filter,
  ManualEmissionTank,
  Month,
  MONTH_MAP,
  MonthlyEmissionControl,
  OptionFilter,
  RowData,
  TankEmission,
  TankManual,
} from "src/entity/tank/tank.entity";
import { CommonError, CommonSuccess } from "src/entity/common/menus-enum.enum";
import { calculateReportVocEmission, getManualTank, getOptions, updateManualTank } from "src/services/tank.service";
import { profileStore } from "src/stores/profile-store";
import { useShallow } from "zustand/react/shallow";
import ManualTankModal from "./ManualTankModal";
import { companyStore } from "src/stores/company-store";
import { tankStore } from "src/stores/tank-store";
import { formatNumber, parseAndFormatScientificNumber } from "src/utils/process-number";
import { generateYearArray } from "src/utils/array";
import { MONTHS_LISTS } from "src/entity/common/time.constant";
import { addCenterAlign } from "src/components/AntdCustomPart/TableTitle.tsx/TableTitle";

type OnChange = NonNullable<TableProps<any>["onChange"]>;
type Filters = Parameters<OnChange>[1];
const { Text } = Typography;

const ManualTankTable = () => {
  const profile = profileStore(useShallow((state) => state.profile));
  const loadingProfile = profileStore(useShallow((state) => state.loading));
  const { permissions } = useNavigationMenu();
  const [dataTable, setDataTable] = useState<DataTable>([]);
  const [dataEmissionControlTable, setDataEmissionControlTable] = useState<DataTable>([]);
  const [dataModal, setDataModal] = useState<DataTable>([]);
  const [loading, setLoading] = useState(false);
  const [companyId, setCompanyId] = useState<string>("");
  const [data, setData] = useState<TankManual[]>();
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [optionYears, setOptionYears] = useState<number[]>([]);
  const currentYear = new Date().getFullYear();
  const [messageApi, contextHolder] = message.useMessage();
  const [current, setCurrent] = useState(1);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState<Filters>({});
  const pageSize = 10;
  const [showModalManualTank, setShowModalManualTank] = useState(false);

  // property for modal
  const [rawDataProp, setRawDataProp] = useState<TankManual>();
  const [dataTableProp, setDataTableProps] = useState<DataTable>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [optionsFilter, setOptionFilter] = useState<OptionFilter[]>([]);

  const getAreaByCompanyId = companyStore(useShallow((state) => state.getAreaByCompanyId));
  const areaMaster = companyStore(useShallow((state) => state.dataArea));

  const getTankTypeMaster = tankStore(useShallow((state) => state.getTankType));
  const tankTypeMaster = tankStore(useShallow((state) => state.tankType));

  const getTankEmissionControlMaster = tankStore(useShallow((state) => state.getTankEmissionControl));
  const tankEmissionControlMaster = tankStore(useShallow((state) => state.tankEmissionControl));

  const months = MONTH_MAP;

  const fetchYear = () => {
    const years = generateYearArray();
    setOptionYears(years as number[]);
    setSelectedYear(years?.[0] as number);
  };

  const calPagination = (current?: number) => {
    if (current) {
      return (current - 1) * 10;
    }
  };

  const createParamFilter = () => {
    let queryParam = "";
    let offset = 0;
    let limit = pageSize;
    if (selectedYear) {
      queryParam += `year=${selectedYear}`;
    }
    if (current) {
      let resultCalOffset = calPagination(current);
      if (resultCalOffset) {
        offset = resultCalOffset;
      }
      queryParam += `&offset=${offset}`;
    }
    if (limit) {
      queryParam += `&limit=${limit}`;
    }

    if (filter) {
      const str = Object.entries(filter)
        .filter(([, value]) => value)
        .map(([key, value]) => `&${key}=[${value?.join(",")}]`)
        .join("");
      queryParam += str;
    }

    return queryParam;
  };

  const fetchOption = async () => {
    try {
      if (selectedYear) {
        const response = await getOptions(selectedYear);
        if (response.data.code === 200) {
          const data = response.data.data.filter;
          setOptionFilter(data);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveManualTank = async (data: TankManual) => {
    try {
      if (selectedYear) {
        setLoading(true);
        data = {
          ...data,
          year: selectedYear,
          companyId: companyId,
        };
        let response = await updateManualTank(selectedId, data);
        if (response.data.code === 200) {
          messageApi.open({
            type: "success",
            content: CommonSuccess.SAVED_SUCCESSFULL,
          });
          fetchManualTank();
          setShowModalManualTank(false);
          setLoading(false);
          processCalculateReport(data._id ? data._id : "");
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const processCalculateReport = async (id: string) => {
    try {
      setLoading(true);
      await calculateReportVocEmission(id);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchManualTank = async () => {
    try {
      if (selectedYear) {
        setLoading(true);
        let offset = 0;
        let resultCalOffset = calPagination(current);
        if (resultCalOffset) {
          offset = resultCalOffset;
        }
        const queryParam = createParamFilter();
        const response = await getManualTank(queryParam);
        if (response.data.code === 200) {
          setLoading(false);
          const manualTank = response.data.data.data.flatMap((el: any, index: number) => {
            const { emissionControlDetails } = el;
            return emissionControlDetails.map((emissionItem: any) => {
              const { emissionControlId } = emissionItem;
              return {
                ...el,
                no: (index + offset + 1).toString(),
                emissionControlId,
              };
            });
          });

          setData([...manualTank]);
          setTotal(response.data.data.total);
          const manualTankModal = response.data.data.data.flatMap((el: any, index: number) => {
            return {
              ...el,
              no: (index + offset + 1).toString(),
            };
          });

          const rowData: RowData[] = [];
          const rowDataModal: RowData[] = [];
          const rowDataEmissionControl: RowData[] = [];
          manualTank.forEach((tank: TankManual, index: number) => {
            const emissionControlDetails = tank.emissionControlDetails || [];
            const emissionControlTransform: RowData[] = transformEmissionControl(emissionControlDetails);
            // console.log({ emissionControlTransform });
            rowDataEmissionControl.push(...emissionControlTransform);

            const dataTransform: DataTable = transformData(tank.manualEmissionTank, emissionControlTransform);
            dataTransform.forEach((el, idxtf) => {
              const tmp = {
                ...tank,
                index: idxtf,
                ...el,
              };
              rowData.push(tmp);
            });
          });
          setDataTable([...rowData]);
          setDataEmissionControlTable([...rowDataEmissionControl]);
          manualTankModal.forEach((tank: TankManual, index: number) => {
            const emissionControlDetails = tank.emissionControlDetails || [];
            const emissionControlTransform: RowData[] = transformEmissionControl(emissionControlDetails);
            // console.log({ emissionControlTransform });
            const dataTransform: DataTable = transformData(tank.manualEmissionTank, emissionControlTransform);
            dataTransform.forEach((item, idxtf) => {
              const tmp = {
                ...tank,
                ...item,
                index: idxtf,
              };
              rowDataModal.push(tmp);
            });
          });
          setDataModal([...rowDataModal]);
        } else {
          messageApi.open({
            type: "error",
            content: CommonError.SOMETHING_WRONG,
          });
        }
      }
    } catch (error: any) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onOpenModal = (tankId: string) => {
    const tankDataTable = dataModal?.filter((el) => el._id == tankId);
    const tankRawData = data?.find((el) => el._id === tankId);
    if (tankDataTable) {
      setRawDataProp(tankRawData);
      setDataTableProps(tankDataTable);
      setShowModalManualTank(true);
    }
    if (tankId) {
      setSelectedId(tankId);
    }
  };

  const transformData = (manualEmissionTank: ManualEmissionTank, emissionControlFormatted: RowData[]): DataTable => {
    const sort: (keyof TankEmission)[] = ["tvoc", "bz", "bd", "vcm", "edc"];
    const resultParam = Object.keys(manualEmissionTank.jan.emission) as (keyof TankEmission)[];
    const sortedResultParam = resultParam.sort((a, b) => sort.indexOf(a) - sort.indexOf(b));

    const parameters: (keyof TankEmission)[] = sortedResultParam;
    const months: (keyof ManualEmissionTank)[] = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
    const dataTable: DataTable = parameters.map((param) => {
      let unit = "";
      let parameterLabel = param.toUpperCase();

      if (["tvoc", "bz", "bd", "vcm", "edc"].includes(param)) {
        unit = "lb";
      }
      let rowData: RowData = {
        key: param,
        parameter: parameterLabel,
        type: "emission",
        unit: unit,
        jan: 0,
        feb: 0,
        mar: 0,
        apr: 0,
        may: 0,
        jun: 0,
        jul: 0,
        aug: 0,
        sep: 0,
        oct: 0,
        nov: 0,
        dec: 0,
        total: 0,
      };

      let total = 0;
      months.forEach((month) => {
        const monthlyData = manualEmissionTank[month];
        const value = monthlyData.emission[param]?.value || 0;

        if (typeof value === "number") {
          rowData[month] = value ?? 0;
          if (value) {
            total += value;
          }
        }
      });

      rowData.total = total ?? 0;
      return rowData;
    });

    return [...emissionControlFormatted, ...dataTable];
  };

  const transformEmissionControl = (emissionControlDetail: EmissionControlDetail[] | undefined) => {
    const months = MONTHS_LISTS;
    const dataMonthly: any = [];
    const countEmissionControl = emissionControlDetail?.length ?? 0;
    emissionControlDetail?.forEach((item) => {
      if (item.emissionControlId) {
        let total = 0;
        const parameter = getMasterLabel("emissionControlId", item.emissionControlId);
        console.log({ parameter });
        const rowData: any = {
          key: item?.emissionControlId ? item.emissionControlId : "",
          parameter: item?.emissionControlId ? item.emissionControlId : "",
          unit: item.unit ?? "%",
          type: "emissionControl",
        };

        months.forEach((month) => {
          const key = month as keyof MonthlyEmissionControl;
          const monthlyData = item?.monthlyData?.[key]?.value;
          rowData[month] = monthlyData;
          // if (countEmissionControl === 1) {
          //   rowData[month] = 100;
          // } else if (monthlyData) {
          //   rowData[month] = monthlyData;
          // } else {
          //   rowData[month] = 0;
          // }
          total += rowData[month] ?? 0;
        });
        rowData["total"] = total ?? 0;
        dataMonthly.push(rowData);
      }
    });

    return dataMonthly;
  };

  const handleYearChange = (value: number) => {
    setSelectedYear(value);
  };

  const initMaster = async () => {
    const query = "";
    try {
      if (profile?.activeCompany._id) {
        setLoading(true);
        await Promise.all([getTankTypeMaster(query), getTankEmissionControlMaster(query)]);
        setCompanyId(profile?.activeCompany._id);
        setLoading(false);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const resetFilter = () => {
    setFilter({});
  };

  useEffect(() => {
    initMaster();
    fetchYear();
  }, []);

  useEffect(() => {
    if (profile?.activeCompany) {
      const fetchData = async () => {
        const companyId = profile.activeCompany._id;
        setSelectedYear(currentYear);
        resetFilter();
        setCurrent(1);
        await Promise.all([getAreaByCompanyId(companyId)]);
        setCompanyId(companyId);
      };
      fetchData();
    }
  }, [profile?.activeCompany]);

  useEffect(() => {
    if (loadingProfile === true) {
      setLoading(true);
    }
  }, [loadingProfile]);

  useEffect(() => {
    resetFilter();
    setCurrent(1);
  }, [selectedYear]);

  useEffect(() => {
    const fetchData = async () => {
      if (selectedYear) {
        await Promise.all([fetchManualTank(), fetchOption()]);
      }
    };
    fetchData();
  }, [current, filter]);

  const mapFilter = (key: string): Filter[] | [] => {
    const resultFilter = optionsFilter.find((item) => item.key === key)?.filter;
    if (resultFilter) {
      const filter: Filter[] = resultFilter?.map((value: any) => {
        let text = value;
        if (key === "area") {
          text = getMasterLabel("area", text);
        } else if (key === "tankTypeId") {
          text = getMasterLabel("tankTypeId", text);
        } else if (key === "emissionControlId") {
          text = getMasterLabel("emissionControlId", text);
        } else if (key === "efficiency") {
          text = getMasterLabel("efficiency", text);
        } else if (key === "emission") {
          text = String(value).toUpperCase();
          text = text === "TVOC" ? "TVOCs" : text;
        }
        return {
          text,
          value: value,
        };
      });
      return filter;
    } else {
      return [];
    }
  };

  const getMasterLabel = (type: string, text: string) => {
    if (!text || typeof text != "string") return "";
    if (type === "area" && areaMaster) {
      const optValue = areaMaster.find((item) => item._id === text);
      return optValue?.areaName;
    } else if (type === "tankTypeId") {
      const optValue = tankTypeMaster.find((item) => item._id === text);
      return optValue?.tankType;
    } else if (type === "emissionControlId" && tankEmissionControlMaster) {
      const optValue = tankEmissionControlMaster.find((item) => item._id === text);
      return optValue?.emissionControl;
    } else if (type === "efficiency") {
      const optValue = tankEmissionControlMaster.find((item) => item._id === text);
      return optValue?.efficiency;
    } else {
      return text;
    }
  };

  const monthColumns: TableColumnsType<ManualEmissionTank> = months.map((month) => ({
    title: month.title,
    render: (_, record: ManualEmissionTank) => {
      if (record && typeof record === "object") {
        const value = record[month.dataIndex as keyof ManualEmissionTank];
        return <Text>{typeof value === "number" ? formatNumber(value) || 0 : 0}</Text>;
      }
      return <Text>0</Text>;
    },
  }));

  const actionColumns: TableColumnsType = [
    {
      title: "Action",
      key: "action",
      render: (text, record, index) => {
        const rowSpan = getRowSpan(index, record, dataTable);
        return {
          children: (
            <div>
              <Button
                className="border-transparent bg-transparent"
                disabled={!canEditTable(permissions)}
                onClick={() => {
                  onOpenModal(record._id);
                }}
              >
                <BsPencilSquare size={18} color={componentPalette.blueGrey} />
              </Button>
            </div>
          ),
          props: {
            rowSpan,
          },
        };
      },
    },
  ];

  const onChangePagination: PaginationProps["onChange"] = (page) => {
    setCurrent(page);
  };

  const onTableChange: TableProps["onChange"] = (pagination: any, filters: any) => {
    let current = pagination.current;
    setFilter(filters);
    setCurrent(current);
  };

  const getRowSpan = (index: number, record: any, dataTable: any[]) => {
    if (index === 0 || dataTable[index - 1]._id !== record._id) {
      return dataTable.filter((item) => item._id === record._id).length;
    }
    return 0;
  };

  const subTableColumns: TableColumnsType = [
    {
      title: "No",
      dataIndex: "no",
      width: 60,
      render: (text, record, index) => {
        const rowSpan = getRowSpan(index, record, dataTable);
        return {
          children: <Text>{text}</Text>,
          props: {
            rowSpan,
          },
        };
      },
    },
    {
      title: "Tank ID",
      dataIndex: "tankId",
      width: 100,
      filteredValue: filter?.tankId || null,
      filters: mapFilter("tankId"),
      render: (text, record, index) => {
        const rowSpan = getRowSpan(index, record, dataTable);
        return {
          children: <Text>{text}</Text>,
          props: {
            rowSpan,
          },
        };
      },
    },
    {
      title: "Area",
      dataIndex: "area",
      key: "area",
      width: 60,
      filteredValue: filter?.area || null,
      filters: mapFilter("area"),
      render: (text, record, index) => {
        const rowSpan = getRowSpan(index, record, dataTable);
        return {
          children: <Text>{getMasterLabel("area", text)}</Text>,
          props: {
            rowSpan,
          },
        };
      },
    },
    {
      title: "Chemical Name",
      dataIndex: "chemicalName",
      width: 150,
      filteredValue: filter?.chemicalName || null,
      filters: mapFilter("chemicalName"),
      render: (text, record, index) => {
        const rowSpan = getRowSpan(index, record, dataTable);
        return {
          children: <Text>{text}</Text>,
          props: {
            rowSpan,
          },
        };
      },
    },
    {
      title: "Tank Type",
      dataIndex: "tankType",
      key: "tankTypeId",
      width: 100,
      filteredValue: filter?.tankTypeId || null,
      filters: mapFilter("tankTypeId"),
      render: (text, record, index) => {
        const rowSpan = getRowSpan(index, record, dataTable);
        return {
          children: <Text>{text}</Text>,
          props: {
            rowSpan,
          },
        };
      },
    },
    {
      title: "Emission Control",
      dataIndex: "emissionControlId",
      key: "emissionControlId",
      width: 120,
      render: (text, record, index) => {
        let rowSpan = 1;
        if (index === 0 || dataTable[index - 1]._id !== record._id || dataTable[index - 1].emissionControlId != record.emissionControlId) {
          rowSpan = dataTable.filter((item) => item._id === record._id && item.emissionControlId === record.emissionControlId).length;
        } else {
          rowSpan = 0;
        }
        return {
          children: <Text>{getMasterLabel("emissionControlId", text)}</Text>,
          props: {
            rowSpan,
          },
        };
      },
    },
    {
      title: "Efficiency (%)",
      dataIndex: "emissionControlId",
      width: 120,
      render: (text, record, index) => {
        let rowSpan = 1;
        if (index === 0 || dataTable[index - 1]._id !== record._id || dataTable[index - 1].emissionControlId != record.emissionControlId) {
          rowSpan = dataTable.filter((item) => item._id === record._id && item.emissionControlId === record.emissionControlId).length;
        } else {
          rowSpan = 0;
        }
        return {
          children: <Text>{getMasterLabel("efficiency", text)}</Text>,
          props: {
            rowSpan,
          },
        };
      },
    },
    {
      title: "Emission",
      dataIndex: "parameter",
      key: "emission",
      width: 100,
      filteredValue: filter?.emission || null,
      filters: mapFilter("emission"),
      // render: (text) => <Text>{text === "TVOC" ? "TVOCs" : text}</Text>,
      render: (text, record) => {
        if (record.type === "emission") {
          return <Text>{text === "TVOC" ? "TVOCs" : text}</Text>;
        } else {
          return <Text>{getMasterLabel("emissionControlId", text)}</Text>;
        }
      },
    },
    { title: "Unit", dataIndex: "unit", key: "unit", render: (text) => <Text>{text}</Text> },
  ];

  const totalColumn: TableColumnsType = [
    {
      title: "Total",
      dataIndex: "total",
      render: (value) => {
        return <Text>{typeof value === "number" ? parseAndFormatScientificNumber(`${value}`, 2) || 0 : 0}</Text>;
      },
    },
  ];

  const columns = [...subTableColumns, ...monthColumns, ...totalColumn, ...actionColumns];
  return (
    <div>
      {contextHolder}
      {showModalManualTank && rawDataProp && (
        <ManualTankModal
          open={true}
          rawData={rawDataProp}
          dataTable={dataTableProp}
          areaMaster={areaMaster}
          tankTypeMaster={tankTypeMaster}
          tankEmissionControlMaster={tankEmissionControlMaster}
          loading={loading}
          onCancel={() => {
            setShowModalManualTank(false);
          }}
          onSave={(data) => {
            handleSaveManualTank(data);
          }}
        />
      )}
      <div style={{ display: "flex", justifyContent: "space-between", paddingBottom: "10px" }}>
        <div>
          <Select
            value={selectedYear}
            style={{ width: 120 }}
            onChange={handleYearChange}
            options={optionYears.map((year) => ({ label: year, value: year }))}
            loading={loading}
            size="middle"
          />
        </div>
      </div>
      <Table
        className="w-full mt-4"
        style={{ whiteSpace: "nowrap" }}
        columns={addCenterAlign(columns)}
        dataSource={canViewTable(permissions) ? dataTable : []}
        scroll={{ x: "max-content" }}
        rowKey={(record, index) => `${record._id}-${index}`}
        pagination={false}
        onChange={onTableChange}
        loading={loading}
      />
      <Pagination
        size={"small"}
        showSizeChanger={false}
        style={{ display: "flex", justifyContent: "right", padding: "7px" }}
        current={current}
        total={total}
        onChange={onChangePagination}
      />
    </div>
  );
};

export default ManualTankTable;
